import { t } from '@lingui/core/macro';
import { useCallback, useMemo, useState } from 'react';
import { TypeId, launchWizard, type Project } from '@sdk';
import { notify } from '@src/notifications';
import { invalidateGitPreflight, runGitShareFix, useGitSharePreflight } from '@src/hooks/use-git-share-preflight';
import { useProjectDependencies } from '@src/hooks/use-project-dependencies';
import { gitShareGateState, type GitShareGateState } from '@src/components/share-to-conversation/git-share-gate-state';
import type { DependencyFolderTarget } from '@src/hooks/use-dependency-for-rel';

export interface GitShareGate {
  /** Which face the gate renders — `checking` until the backend answers. */
  state: GitShareGateState;
  /** The backend's human-readable reason (null when available). */
  reason: string | null;
  /** True while a remediation (setup / commit+push) is running. */
  busy: boolean;
  /** Run the git-setup wizard, then re-check. */
  runSetup: () => Promise<void>;
  /** Commit AND push, then re-check. */
  runCommit: () => Promise<void>;
}

/**
 * The pre-share gate for a folder (a dependency, or a folder of the project's own): preflight → remediate → re-check.
 *
 * Re-checking is EVENT-driven, never polled: each remediation calls the
 * preflight's `refetch` once it settles — one re-check, on an explicit
 * completion. No interval, no backoff, no retry budget.
 */
export function useGitShareGate(
  folder: DependencyFolderTarget | null,
  project: Project | null | undefined,
  enabled: boolean,
): GitShareGate {
  const [setupBusy, setSetupBusy] = useState(false);
  const { add } = useProjectDependencies(project, { fetchStates: false });

  const ref = useMemo(() => (folder?.typeid ? new TypeId(folder.typeid) : undefined), [folder?.typeid]);
  const preflight = useGitSharePreflight(ref, enabled);

  // ONE action, scoped to the shared folder: commit + push only its path (or
  // push what is already committed). The person's other work in the repo is
  // never swept in, and nothing is forced — a conflict is reported back.
  const [pushBusy, setPushBusy] = useState(false);
  const runCommit = useCallback(async () => {
    if (!ref) return;
    setPushBusy(true);
    try {
      const res = await runGitShareFix(ref, preflight.fix ?? 'commit');
      if (!res.ok) notify.error({ title: t`Could not commit and push`, message: res.message });
    } catch (e) {
      notify.error({ title: t`Could not commit and push`, message: String(e) });
    } finally {
      setPushBusy(false);
      preflight.refetch();
    }
  }, [ref, preflight]);

  const runSetup = useCallback(async () => {
    if (!folder || !project) return;
    setSetupBusy(true);
    try {
      const result = await launchWizard<{ path?: string }>('git-dependency', {
        title: t`Set up Git for sharing`,
        targetTypeId: project.typeId.toString(),
        payload: {
          projectId: project.id,
          optional: !folder.required,
          mode: 'adopt',
          path: folder.workdir,
          name: folder.name,
        },
        prompt:
          `The user wants to share the folder "${folder.name}" (${folder.workdir}), but it ` +
          `isn't backed by a git repository with an "origin" remote yet, so it can't be shared. ` +
          `Adopt THAT EXACT FOLDER in place — do not clone it, copy it, or create a repository ` +
          `anywhere else. Initialize git in ${folder.workdir} if needed, commit its current ` +
          `contents, create the "origin" remote, and push. Report when the folder has a working ` +
          `origin remote and a pushed commit.`,
      });
      if (result.status === 'error') {
        notify.error({ title: t`Could not set up Git`, message: result.errorStr ?? undefined });
        return;
      }
      if (result.status !== 'done') return;
      // Re-register so the backend re-runs `detect_origin`: a Folder's identity
      // IS its origin key, so a now-git directory must be re-minted, not mutated
      // in place — otherwise it keeps its stale LocalOrigin forever and every
      // later preflight re-probes a folder the graph still calls local. A
      // dependency is re-added from its folder (a folder inside git is written
      // as its repo, under the same name); a folder of the project's own is
      // only re-minted — sharing it must not make it a dependency.
      if (folder.dependency) {
        await add(folder.workdir, folder.local ? 'local' : folder.required ? 'required' : 'optional');
      } else {
        await project.folderForPath(folder.workdir);
      }
    } catch (e) {
      notify.error({ title: t`Could not set up Git`, message: String(e) });
    } finally {
      setSetupBusy(false);
      // This gate's own answer, and every other surface looking at the same
      // folder — the git state changed on disk, which nothing else announces.
      preflight.refetch();
      invalidateGitPreflight(ref);
    }
  }, [folder, project, add, preflight, ref]);

  const busy = pushBusy || setupBusy;
  return {
    // IDLE and AVAILABLE both carry `code: null`, so `answered` — not the code —
    // is what separates "not asked yet" from "asked, fine".
    state: preflight.loading || busy || !preflight.answered ? 'checking' : gitShareGateState(preflight.code),
    reason: preflight.reason,
    busy,
    runSetup,
    runCommit,
  };
}
